import {
  Button,
  ButtonGroup,
  Divider,
  Flex,
  FlexProps,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  Text,
  useBreakpointValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import axios from 'axios';
import { useCallback, useMemo, useState } from 'react';
import {
  RiBookmarkLine,
  RiDeleteBinLine,
  RiEditLine,
  RiMoneyDollarCircleLine,
} from 'react-icons/ri';
import { useHistory } from 'react-router-dom';
import { ConfirmationModal } from '../../../../../../../../components/ConfirmationModal';
import { LinkButton } from '../../../../../../../../components/LinkButton';
import { AppointmentPaymentConfirmationModal } from '../../../../../../../../components/AppointmentPaymentConfirmationModal';
import UserInfo from '../../../../../../../../components/UserInfo';
import {
  IAppointmentContextState,
  useAppointment,
} from '../../../../../../../../hooks/appointment';
import { useAuth } from '../../../../../../../../hooks/auth';
import { PrettyAppointmentPaymentStatus } from '../../../../../../../../models/appointments';
import { createAppointmentExternalPaymentsService } from '../../../../../../../../services/Appointments/CreateAppointmentExternalPaymentsService';
import deleteAppointmentsService from '../../../../../../../../services/Appointments/DeleteAppointmentsService';
import { translateError } from '../../../../../../../../utils/errors';
import { IHandleAppointmentPayment } from '../../../../components/AppointmentTable';

export const AppointmentDetailsInfo = (props: FlexProps): JSX.Element => {
  const toast = useToast();

  const { assignment: authenticatedAssignment } = useAuth();

  const { appointment, handleAppointment } = useAppointment();

  const { push } = useHistory();

  const dividerOrientation = useBreakpointValue({
    base: 'horizontal',
    xl: 'vertical',
  });

  const appointmentFinished = useMemo(
    () =>
      !!appointment &&
      ![
        'WAITING',
        'FAILED',
        'REJECTED',
        'PAYMENT_EXTERNAL_PENDING',
        'FREE',
      ].includes(appointment.paymentStatus),
    [appointment],
  );

  const { isPaymentButtonDisabled, paymentButtonColorScheme } = useMemo(() => {
    const isDisabled =
      appointmentFinished ||
      !authenticatedAssignment?.featureGroup.features.some(
        (feature) =>
          ![
            'APPOINTMENT_PAYMENT_FULL_ACCESS',
            'APPOINTMENT_PAYMENT_WRITE_ACCESS',
          ].includes(feature.key),
      );

    let colorScheme: string = isDisabled ? 'green' : 'red';

    if (appointment?.paymentStatus === 'PAYMENT_EXTERNAL_PENDING') {
      colorScheme = 'yellow';
    }

    return {
      isPaymentButtonDisabled: isDisabled,
      paymentButtonColorScheme: colorScheme,
    };
  }, [
    appointment,
    appointmentFinished,
    authenticatedAssignment?.featureGroup.features,
  ]);

  const [handlingAppointment, setHandlingAppointment] =
    useState<IAppointmentContextState>();
  const [
    isDeleteConfirmationModalVisible,
    setIsDeleteConfirmationModalVisible,
  ] = useState(false);
  const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);

  const toggleDeleteConfirmationModal = useCallback(
    (appnt?: IAppointmentContextState) => {
      setHandlingAppointment(appnt);
      setIsDeleteConfirmationModalVisible((prevState) => !prevState);
    },
    [],
  );

  const cancelAppointmentWarningMessage = useMemo(() => {
    if (!handlingAppointment) {
      return undefined;
    }

    if (handlingAppointment?.paymentStatus === 'PAYMENT_EXTERNAL') {
      return 'Essa ordem de serviço foi paga no clube, o reembolso deverá ser feito externamente.';
    }

    return undefined;
  }, [handlingAppointment]);

  const togglePaymentModal = useCallback((appnt?: IAppointmentContextState) => {
    setHandlingAppointment(appnt);

    setIsPaymentModalVisible((prevState) => !prevState);
  }, []);

  const handlePayment = useCallback(
    async ({ appointmentId, payment }: IHandleAppointmentPayment) => {
      if (!payment) {
        return;
      }

      try {
        const paidAppointment = await createAppointmentExternalPaymentsService({
          appointmentId,
          payment: {
            authorizationCode: payment.authorizationCode,
            authorizedAt: payment.authorizedAt,
            type: payment.type,
            acquirerName: payment.acquirerName,
            acquirerNsu: payment.acquirerNsu,
            brand: payment.brand,
          },
        });

        if (appointment) {
          handleAppointment({
            ...paidAppointment,
            titularUser: appointment.titularUser,
          });
        }

        togglePaymentModal(undefined);

        toast({
          title: 'Transação concluída com sucesso',
          description: 'A transação foi efetuada corretamente.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          if (err.response?.data.message === 'user-address-missing') {
            toast({
              title: 'Cadastro incompleto',
              description: `Para registrar o pagamento será necessário completar o cadastro do membro. ${translateError(
                { message: err.response?.data.message },
              )}`,
              status: 'warning',
              duration: 3000,
              isClosable: true,
              variant: 'subtle',
              position: 'top-right',
            });

            return;
          }

          toast({
            title: 'Falha na transação',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao efetuar a transação, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [appointment, toast, handleAppointment, togglePaymentModal],
  );

  const handleDeleteAppointment = useCallback(async () => {
    if (appointment?.id) {
      try {
        await deleteAppointmentsService(appointment.id);

        toast({
          title: 'Excluído com sucesso',
          description: 'A ordem de serviço foi excluída corretamente.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });

        push('/services');
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toggleDeleteConfirmationModal();

          toast({
            title: 'Falha ao excluir',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao excluir a ordem de serviço, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    }
  }, [appointment?.id, toggleDeleteConfirmationModal, push, toast]);

  return (
    <>
      {!!handlingAppointment && (
        <AppointmentPaymentConfirmationModal
          isOpen={isPaymentModalVisible}
          onClose={() => togglePaymentModal(undefined)}
          onConfirm={(payment) =>
            handlePayment({
              appointmentId: handlingAppointment.id,
              payment,
            })
          }
          user={handlingAppointment.user}
        />
      )}

      {!!handlingAppointment && (
        <ConfirmationModal
          isOpen={!!isDeleteConfirmationModalVisible}
          onClose={toggleDeleteConfirmationModal}
          onConfirm={handleDeleteAppointment}
          title="Confirmar cancelamento"
          message="Deseja realmente cancelar?"
          warningMessage={cancelAppointmentWarningMessage}
        />
      )}

      <Flex
        direction="column"
        w="100%"
        borderRadius={8}
        bg="white"
        p="8"
        {...props}
      >
        <Flex justify="space-between" align="center">
          <Heading size="lg" fontWeight="normal">
            Detalhes da ordem de serviço
          </Heading>
        </Flex>

        {!!appointment && (
          <Flex direction="column" mt="8">
            <UserInfo ml="8" px="8" title="Usuário" user={appointment.user} />

            {appointment.titularUser.id !== appointment.userId && (
              <>
                <Divider borderColor="gray.300" my="8" />

                <UserInfo
                  ml="8"
                  px="8"
                  title="Responsável financeiro"
                  user={appointment.titularUser}
                />
              </>
            )}

            <Divider borderColor="gray.300" my="8" />

            <Grid
              templateColumns={[
                'repeat(1, 1fr)',
                'repeat(1, 1fr)',
                'repeat(1, 1fr)',
                'repeat(1, 1fr)',
                'repeat(12, 1fr)',
              ]}
              gap="4"
              width="100%"
            >
              <GridItem colSpan={[12, 12, 12, 12, 8]}>
                <Flex direction="column" px="8">
                  <Flex alignItems="flex-start">
                    <VStack flex="1" alignItems="flex-start" ml="8">
                      <HStack>
                        <Text>#OS:</Text>
                        <Text color="gray.500">
                          {String(appointment.code).padStart(6, '0')}
                        </Text>
                      </HStack>

                      <HStack>
                        <Text>Valor total:</Text>
                        <Text color="gray.500">
                          {appointment.formattedTotalValue}
                        </Text>
                      </HStack>

                      <HStack>
                        <Text>Status pagamento:</Text>
                        <Text color="gray.500">
                          {
                            PrettyAppointmentPaymentStatus[
                              appointment.paymentStatus
                            ]
                          }
                        </Text>
                      </HStack>

                      <HStack>
                        <Text>Cadastrado em:</Text>
                        <Text color="gray.500">
                          {appointment.formattedCreatedAt}
                        </Text>
                      </HStack>
                    </VStack>

                    <Divider
                      hidden={dividerOrientation === 'horizontal'}
                      ml="4"
                      alignSelf="center"
                      height="80%"
                      borderColor="gray.300"
                      orientation="vertical"
                    />
                  </Flex>

                  <ButtonGroup mt="8" ml="8">
                    {/* <LinkButton
                      colorScheme="green"
                      icon={<Icon as={RiRefreshLine} fontSize="18" />}
                      to={{
                        pathname: '/services/appointments/management',
                        state: {
                          appointmentId: appointment.id,
                        },
                      }}
                    >
                      Recontratar
                    </LinkButton> */}

                    {!!appointment.totalValue &&
                      authenticatedAssignment?.featureGroup.features.some(
                        (feature) =>
                          [
                            'APPOINTMENT_PAYMENT_FULL_ACCESS',
                            'APPOINTMENT_PAYMENT_WRITE_ACCESS',
                          ].includes(feature.key),
                      ) && (
                        <Button
                          size="sm"
                          fontSize="sm"
                          colorScheme={paymentButtonColorScheme}
                          color="white"
                          disabled={isPaymentButtonDisabled}
                          onClick={() => togglePaymentModal(appointment)}
                          leftIcon={
                            <Icon as={RiMoneyDollarCircleLine} fontSize="18" />
                          }
                        >
                          Pagamento
                        </Button>
                      )}

                    {!appointmentFinished &&
                      authenticatedAssignment?.featureGroup.features.some(
                        (feature) =>
                          [
                            'APPOINTMENT_FULL_ACCESS',
                            'APPOINTMENT_WRITE_ACCESS',
                          ].includes(feature.key),
                      ) && (
                        <LinkButton
                          colorScheme="yellow"
                          color="white"
                          icon={<Icon as={RiEditLine} fontSize="16" />}
                          to={{
                            pathname: '/services/appointments/management',
                            state: {
                              appointmentId: appointment.id,
                            },
                          }}
                        >
                          Editar
                        </LinkButton>
                      )}

                    {appointment.paymentStatus !== 'CANCELED' &&
                      authenticatedAssignment?.featureGroup.features.some(
                        (feature) =>
                          [
                            'APPOINTMENT_FULL_ACCESS',
                            'APPOINTMENT_DELETE_ACCESS',
                          ].includes(feature.key),
                      ) && (
                        <Button
                          size="sm"
                          fontSize="sm"
                          colorScheme="red"
                          onClick={() =>
                            toggleDeleteConfirmationModal(appointment)
                          }
                          leftIcon={<Icon as={RiDeleteBinLine} fontSize="20" />}
                        >
                          Excluir
                        </Button>
                      )}
                  </ButtonGroup>
                </Flex>
              </GridItem>

              {appointment.description && (
                <GridItem colSpan={[12, 12, 12, 12, 4]}>
                  <Flex height="100%">
                    <Divider
                      hidden={dividerOrientation === 'horizontal'}
                      px="2"
                      height="80%"
                      borderColor="gray.300"
                      orientation="vertical"
                    />

                    <Flex flexDirection="column" w="100%">
                      <Divider
                        hidden={dividerOrientation === 'vertical'}
                        borderColor="gray.300"
                        mt="4"
                        mb="8"
                      />
                      <Flex
                        flexDirection="column"
                        px={[8, 8, 8, 8, 0]}
                        w="100%"
                      >
                        <HStack mt={[0, 0, 0, 0, 2]}>
                          <Icon as={RiBookmarkLine} fontSize="20" />
                          <Text>Descrição</Text>
                        </HStack>

                        <Text
                          fontSize="sm"
                          color="gray.500"
                          mt="4"
                          px={[4, 4, 4, 4, 0]}
                        >
                          {appointment.description}
                        </Text>
                      </Flex>
                    </Flex>
                  </Flex>
                </GridItem>
              )}
            </Grid>
          </Flex>
        )}
      </Flex>
    </>
  );
};
